export const MAX_UPLOAD_INPUT_FILE_COUNT = 50;
export const MAX_UPLOAD_INPUT_FILE_SIZE_BYTES = 20 * 1024 * 1024;
export const MAX_UPLOAD_INPUT_TOTAL_SIZE_BYTES = 32 * 1024 * 1024;
export const MAX_UPLOAD_INPUT_REQUEST_SIZE_BYTES = MAX_UPLOAD_INPUT_TOTAL_SIZE_BYTES + 1024 * 1024;

interface CanonicalUploadMetadata {
  extension: string;
  mime: string;
}

const METADATA_BY_MIME: Readonly<Record<string, CanonicalUploadMetadata>> = {
  'image/avif': { extension: 'avif', mime: 'image/avif' },
  'image/bmp': { extension: 'bmp', mime: 'image/bmp' },
  'image/gif': { extension: 'gif', mime: 'image/gif' },
  'image/heic': { extension: 'heic', mime: 'image/heic' },
  'image/heif': { extension: 'heif', mime: 'image/heif' },
  'image/jpeg': { extension: 'jpg', mime: 'image/jpeg' },
  'image/jpg': { extension: 'jpg', mime: 'image/jpeg' },
  'image/png': { extension: 'png', mime: 'image/png' },
  'image/tiff': { extension: 'tiff', mime: 'image/tiff' },
  'image/webp': { extension: 'webp', mime: 'image/webp' },
  'image/x-ms-bmp': { extension: 'bmp', mime: 'image/bmp' },
  'video/mp4': { extension: 'mp4', mime: 'video/mp4' },
  'video/mpeg': { extension: 'mpg', mime: 'video/mpeg' },
  'video/quicktime': { extension: 'mov', mime: 'video/quicktime' },
  'video/webm': { extension: 'webm', mime: 'video/webm' },
  'video/x-m4v': { extension: 'm4v', mime: 'video/x-m4v' },
  'video/x-matroska': { extension: 'mkv', mime: 'video/x-matroska' },
  'video/x-msvideo': { extension: 'avi', mime: 'video/x-msvideo' },
  'audio/aac': { extension: 'aac', mime: 'audio/aac' },
  'audio/flac': { extension: 'flac', mime: 'audio/flac' },
  'audio/m4a': { extension: 'm4a', mime: 'audio/mp4' },
  'audio/mp3': { extension: 'mp3', mime: 'audio/mpeg' },
  'audio/mp4': { extension: 'm4a', mime: 'audio/mp4' },
  'audio/mpeg': { extension: 'mp3', mime: 'audio/mpeg' },
  'audio/ogg': { extension: 'ogg', mime: 'audio/ogg' },
  'audio/wav': { extension: 'wav', mime: 'audio/wav' },
  'audio/webm': { extension: 'weba', mime: 'audio/webm' },
  'audio/x-flac': { extension: 'flac', mime: 'audio/flac' },
  'audio/x-m4a': { extension: 'm4a', mime: 'audio/mp4' },
  'audio/x-wav': { extension: 'wav', mime: 'audio/wav' },
};

const METADATA_BY_EXTENSION: Readonly<Record<string, CanonicalUploadMetadata>> = {
  avif: METADATA_BY_MIME['image/avif'],
  bmp: METADATA_BY_MIME['image/bmp'],
  gif: METADATA_BY_MIME['image/gif'],
  heic: METADATA_BY_MIME['image/heic'],
  heif: METADATA_BY_MIME['image/heif'],
  jfif: METADATA_BY_MIME['image/jpeg'],
  jpeg: METADATA_BY_MIME['image/jpeg'],
  jpg: METADATA_BY_MIME['image/jpeg'],
  png: METADATA_BY_MIME['image/png'],
  tif: METADATA_BY_MIME['image/tiff'],
  tiff: METADATA_BY_MIME['image/tiff'],
  webp: METADATA_BY_MIME['image/webp'],
  mp4: METADATA_BY_MIME['video/mp4'],
  mov: METADATA_BY_MIME['video/quicktime'],
  webm: METADATA_BY_MIME['video/webm'],
  m4v: METADATA_BY_MIME['video/x-m4v'],
  mkv: METADATA_BY_MIME['video/x-matroska'],
  avi: METADATA_BY_MIME['video/x-msvideo'],
  mpg: METADATA_BY_MIME['video/mpeg'],
  aac: METADATA_BY_MIME['audio/aac'],
  flac: METADATA_BY_MIME['audio/flac'],
  m4a: METADATA_BY_MIME['audio/mp4'],
  mp3: METADATA_BY_MIME['audio/mpeg'],
  mpeg: METADATA_BY_MIME['audio/mpeg'],
  mpga: METADATA_BY_MIME['audio/mpeg'],
  oga: METADATA_BY_MIME['audio/ogg'],
  ogg: METADATA_BY_MIME['audio/ogg'],
  wav: METADATA_BY_MIME['audio/wav'],
  weba: METADATA_BY_MIME['audio/webm'],
};

function getExtension(fileName: string): string {
  const match = /\.([^.]+)$/.exec(fileName);
  return match?.[1].toLowerCase() ?? '';
}

function getOwnMetadata(
  table: Readonly<Record<string, CanonicalUploadMetadata>>,
  key: string,
): CanonicalUploadMetadata | null {
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
}

function getCanonicalUploadMetadata(file: File): CanonicalUploadMetadata | null {
  const declaredMime = file.type.trim().toLowerCase().split(';', 1)[0];
  const originalExtension = getExtension(file.name);
  // Some OS MIME databases report MPEG audio files as video/mpeg. Preserve
  // the app's established .mpeg/.mpga audio behavior without reclassifying a
  // genuine .mpg video (or an extensionless video/mpeg file) as audio.
  return declaredMime === 'video/mpeg'
    && ['mp3', 'mpeg', 'mpga'].includes(originalExtension)
    ? METADATA_BY_MIME['audio/mpeg']
    : declaredMime
      ? getOwnMetadata(METADATA_BY_MIME, declaredMime)
      : getOwnMetadata(METADATA_BY_EXTENSION, originalExtension);
}

/** Whether the guarded browser endpoint can accept a batch without falling back. */
export function canPreUploadGenerationInputFiles(files: readonly File[]): boolean {
  return files.length > 0
    && files.length <= MAX_UPLOAD_INPUT_FILE_COUNT
    && files.every(file => (
      file.size <= MAX_UPLOAD_INPUT_FILE_SIZE_BYTES
      && getCanonicalUploadMetadata(file) !== null
    ))
    && files.reduce((total, file) => total + file.size, 0) <= MAX_UPLOAD_INPUT_TOTAL_SIZE_BYTES;
}

/**
 * Restrict the standalone pre-upload endpoint to supported passive media and
 * replace client-controlled public object metadata with canonical values.
 */
export function canonicalizeUploadInputFile(file: File, index: number): File | null {
  const metadata = getCanonicalUploadMetadata(file);
  if (!metadata) return null;

  return new File([file], `input-${index}.${metadata.extension}`, {
    type: metadata.mime,
    lastModified: file.lastModified,
  });
}
